import { Button } from '~/components/general/Button'
import { MainLogoIcon } from '~/components/icons/logos'
import { ChevronLeftIcon, CloseIcon } from '~/components/icons'
import { loginClickEvent, signUpClickEvent } from '../HeaderClickEvents/Events'
import { useState } from 'react'
import classNames from 'classnames'
import { fadeInFadeOutTw } from '~/styles/mixins'
import { hexToRgba } from '~/styles/shadows'
import { Colors } from '~/styles/colors'
import { TypeNavigationGroup, TypeNavigationLink } from '~/types/contentful'
import { Asset } from 'contentful'
import MobileGroup from './MobileGroup'

export type MobileMenuProps = {
  userAuthorized: boolean
  closeMenu: () => void
  menuData: TypeNavigationLink<'WITHOUT_UNRESOLVABLE_LINKS', string>[]
}

type MobileGroupField = {
  title: string
  description?: string | undefined
  link: string
  icon?: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined
  groups?: (TypeNavigationGroup<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined)[] | undefined
  isAuthRequired?: boolean | undefined
  label?: string | undefined
  color?: string | undefined
}

export const MobileMenu = ({ userAuthorized, closeMenu, menuData }: MobileMenuProps) => {
  const [hasLevelActive, setHasLevelActive] = useState<boolean>(false)

  const toggleBackButton = (value: boolean) => setHasLevelActive(value)

  const groupMenu = menuData
    ?.filter((item) => item.fields.groups)
    .map((item) => item.fields) as MobileGroupField[]
  const footerMenu = menuData
    ?.filter((item) => !item.fields.groups)
    .map((item) => item.fields) as MobileGroupField[]

  return (
    <nav
      className={classNames(
        'flex xl:hidden top-0 left-0 right-0 bottom-0 flex-col fixed h-fit text-neutral-100 bg-white overflow-auto z-50 max-h-dvh',
        fadeInFadeOutTw(true),
      )}
    >
      <div
        className={`shadow-[0_1px_0_0_${hexToRgba(
          Colors.Neutral100,
          0.1,
        )}] max-xl:px-6 max-md:pr-3 max-md:pl-4 flex items-center justify-between py-0 px-8 h-[var(--header-height)]`}
      >
        <Button
          className={'!px-0 !py-3 !block !w-[128px]'}
          iconRight={<MainLogoIcon className={'text-neutral-100'} />}
          type={'link'}
          color={'link'}
          target={'_self'}
          href={'/'}
          onClick={closeMenu}
        />
        <Button
          iconRight={<CloseIcon className="text-neutral-40" />}
          onClick={closeMenu}
          color={'transparent'}
          className={'w-fit !p-0'}
        />
      </div>
      <div className="flex flex-col gap-9 md:gap-8 px-7 md:px-12 md:my-7 max-md:mt-3 max-md:mb-11">
        {hasLevelActive && (
          <div
            onClick={() => toggleBackButton(false)}
            className="text-blue-50 cursor-pointer items-center flex px-0 py-4 text-mobile-s"
          >
            <ChevronLeftIcon className="w-4 mr-1 transition duration-300 [&>path]:stroke-blue-50 [&>path]:stroke-[2] hover:translate-x-[-5px]" />
            Back
          </div>
        )}
        <div className="flex flex-col p-0 m-0 gap-0 list-none overflow-y-auto">
          {groupMenu?.map((item, index) => (
            <MobileGroup
              key={index}
              data={item}
              closeMenu={closeMenu}
              handleMenuClick={toggleBackButton}
            />
          ))}
        </div>
      </div>

      <div className="max-md:p-7 py-5 px-12 flex flex-col bg-neutral-5">
        {footerMenu?.map((item: { title: string; link: string }) => (
          <Button
            text={item.title}
            href={item.link}
            onClick={closeMenu}
            key={item.link}
            className={'w-fit !h-[38px] !p-0 !text-neutral-90'}
            type={'link'}
            color={'link'}
            target={'_self'}
          />
        ))}
        {userAuthorized ? (
          <Button
            text={'My Dashboard'}
            type={'link'}
            href={'/app'}
            className={'w-fit'}
            target={'_self'}
          />
        ) : (
          <>
            <Button
              text={'Log In'}
              href={'/app/auth/sign-in'}
              onClick={loginClickEvent}
              className={'w-fit !p-0 !text-neutral-90'}
              type={'link'}
              color={'link'}
              target={'_self'}
            />
            <Button
              text={'Sign Up'}
              href={'/app/create-account'}
              onClick={signUpClickEvent}
              className={'w-fit'}
              type={'link'}
              target={'_self'}
            />
          </>
        )}
      </div>
    </nav>
  )
}
